import { useMemo } from 'react';
import { Chess } from 'chess.js';
import { PieceSvg, type Piece } from '../board/pieces';

/**
 * A small, static board used on browse cards and in theory diagrams.
 *
 * Deliberately not the interactive `Chessboard`: this renders dozens at a time
 * on the browse grid, so it skips move generation, selection state and event
 * handlers entirely and is just a grid of squares and pieces.
 */
export default function BoardThumb({
  fen,
  orientation = 'white',
  size = 168,
}: {
  fen: string;
  orientation?: 'white' | 'black';
  size?: number;
}) {
  const squares = useMemo(() => {
    const chess = new Chess();
    try {
      chess.load(fen);
    } catch {
      return null;
    }

    const rows = chess.board();
    const ordered = orientation === 'white' ? rows : [...rows].reverse().map((r) => [...r].reverse());
    return ordered.flatMap((row, rankIndex) =>
      row.map((cell, fileIndex) => ({
        key: `${rankIndex}-${fileIndex}`,
        dark: (rankIndex + fileIndex) % 2 === 1,
        piece: cell ? ({ type: cell.type, color: cell.color } as Piece) : null,
      })),
    );
  }, [fen, orientation]);

  if (!squares) return <div className="thumb thumb--error" style={{ width: size, height: size }} />;

  return (
    <div className="thumb" style={{ width: size, height: size }} aria-hidden="true">
      {squares.map((square) => (
        <div key={square.key} className={`thumb-sq ${square.dark ? 'thumb-sq--dark' : 'thumb-sq--light'}`}>
          {square.piece && <PieceSvg piece={square.piece} />}
        </div>
      ))}
    </div>
  );
}
